import { useCallback, useEffect, useState } from "react";
import {
	Accordion,
	AccordionButton,
	AccordionIcon,
	AccordionItem,
	AccordionPanel,
	Alert,
	AlertIcon,
	Box,
	Text,
} from "@chakra-ui/react";
import { FiPaperclip } from "react-icons/fi";

import { FileUpload } from "newui/components/FileUpload";
import { FILE_UPLOAD_TYPES } from "newui/components/FileUpload/constants/fileUploadTypes";
import type { UploadedFile } from "newui/components/FileUpload/types/UploadedFile.type";
import type { UploadFile } from "types/storage.types";
import type { AllowedFileType } from "newui/components/FileUpload/types/AllowedFileType.types";
import type { QuoteSupportingDocument } from "../../types/quote.type";
import { getQuoteAttachments, deleteQuoteAttachment } from "../../api";

export interface QuoteSupportingDocumentsProps {
	initialDocuments?: QuoteSupportingDocument[];
	inviteId: number;
	isReadOnly: boolean;
	/**
	 * Called with the current document list whenever the user adds/removes an
	 * attachment (`FileUpload`'s `onChange`, filtered to fully-uploaded files —
	 * see `handleFilesChange`). Lets the parent keep its own copy of the quote
	 * model in sync so a later unmount/remount (tab switch) reseeds
	 * `initialDocuments` from current data instead of the stale eager-loaded
	 * value.
	 */
	onSaved?: (documents: QuoteSupportingDocument[]) => void;
	quoteId: number;
}

const ALLOWED_FILE_TYPES: AllowedFileType[] = [
	"pdf",
	"doc",
	"docx",
	"xls",
	"xlsx",
];

const MAX_FILE_SIZE_MB = 50;

const mapToUploadFile = (doc: QuoteSupportingDocument): UploadFile => ({
	id: doc.id,
	file_name: doc.file_name,
	file_path: doc.file_path,
	file_size: doc.file_size,
	file_type: doc.file_type,
});

const QuoteSupportingDocuments: React.FC<QuoteSupportingDocumentsProps> = ({
	inviteId,
	isReadOnly,
	onSaved,
	quoteId,
	initialDocuments,
}) => {
	const [documents, setDocuments] = useState<QuoteSupportingDocument[]>(
		initialDocuments ?? []
	);
	const [isLoaded, setIsLoaded] = useState(!!initialDocuments);

	// `FileUpload`'s internal state (`useFileUploadState`) re-syncs itself from
	// whatever `initialValue` reference it is passed, on every reference change.
	// If we re-derived `initialValue` from `documents` on every render, each
	// `onChange` we cause (via `handleFilesChange` below) would feed a new
	// array reference straight back in, stomping the in-progress upload state
	// FileUpload owns internally (dropping a file mid-upload before it ever
	// gets a `path`/`storage_path`). We deliberately snapshot `initialValue`
	// only once, when the documents finish loading, and never recompute it
	// afterwards — FileUpload's own onChange lifecycle is the sole source of
	// truth for `documents` from that point on. Scoped to this component so we
	// don't touch `useFileUploadState`'s shared re-sync behaviour, which other
	// `FileUpload` consumers rely on (see ctx caller audit in the task summary).
	const [initialUploadFiles, setInitialUploadFiles] = useState<UploadFile[]>(
		() => (initialDocuments ?? []).map(mapToUploadFile)
	);

	useEffect(() => {
		if (isLoaded) {
			return;
		}

		const fetchDocuments = async () => {
			try {
				const docs = await getQuoteAttachments({
					inviteId,
					quoteId,
				});
				setDocuments(docs);
				setInitialUploadFiles(docs.map(mapToUploadFile));
			} catch {
				// Error handled by global interceptor
			} finally {
				setIsLoaded(true);
			}
		};

		fetchDocuments();
	}, [inviteId, quoteId, isLoaded]);

	const handleFilesChange = useCallback(
		(files: UploadedFile[]) => {
			const mapped: QuoteSupportingDocument[] = files
				.filter((f) => f.id && (f.path || f.storage_path))
				.map((f) => ({
					id: f.id!,
					file_name: f.name,
					file_path: f.path ?? "",
					file_size: f.size ?? 0,
					file_type: f.type,
					storage_path: f.storage_path ?? f.path,
				}));
			setDocuments(mapped);
			onSaved?.(mapped);
		},
		[onSaved]
	);

	const handleDelete = useCallback(
		async (fileId: number) => {
			await deleteQuoteAttachment({ inviteId, quoteId }, fileId);
		},
		[inviteId, quoteId]
	);

	const hasDocuments = documents.length > 0;
	const defaultIndex = hasDocuments ? [0] : [];

	if (isReadOnly && !hasDocuments) {
		return null;
	}

	return (
		<div className="mt-6">
			<Accordion
				allowToggle
				defaultIndex={defaultIndex}
			>
				<AccordionItem className="!border-y-0">
					<AccordionButton className="w-fit rounded-md border border-gray-300 bg-white px-4 py-2 hover:!bg-gray-50">
						<FiPaperclip className="mr-2 text-gray-500" />
						<Box
							as="span"
							flex="1"
							textAlign="left"
						>
							<Text className="font-roboto text-sm font-medium text-gray-700">
								Supporting Documents
								{hasDocuments && (
									<span className="ml-2 text-gray-400">
										({documents.length})
									</span>
								)}
							</Text>
						</Box>
						<AccordionIcon />
					</AccordionButton>
					<AccordionPanel className="!px-0 !pt-3 !pb-0">
						<Alert
							status="info"
							variant="subtle"
							className="mb-4 !rounded-md"
						>
							<AlertIcon />
							<Text className="font-roboto text-xs text-gray-600">
								Supporting documents are for reference only and
								will not be used in quote evaluation.
							</Text>
						</Alert>
						<FileUpload
							allowFileTypes={ALLOWED_FILE_TYPES}
							disabled={isReadOnly}
							extraData={{
								rfx_invite_id: inviteId,
								rfq_quote_id: quoteId,
							}}
							initialValue={initialUploadFiles}
							isPreview={isReadOnly}
							maxFileSizeMB={MAX_FILE_SIZE_MB}
							multiple
							onChange={handleFilesChange}
							supportsHardDelete
							uploadType={
								FILE_UPLOAD_TYPES.QUOTE_SUPPORTING_DOCUMENT
							}
						/>
					</AccordionPanel>
				</AccordionItem>
			</Accordion>
		</div>
	);
};

export { QuoteSupportingDocuments };
